import {useEffect} from 'react';
import type {MobileDisplay} from '../../utilStyles/sheet';
import {
  BOTTOM_SHEET_ANIMATION_DURATION_SHORT,
  BOTTOM_SHEET_ANIMATION_EASING,
} from './BottomSheet.constants';

const DISMISS_THRESHOLD_PX = 120;
const RESISTANCE_START_PX = 80;
const RESISTANCE_FACTOR = 0.4;

// Match animation from sheet.ts
const DISMISS_DURATION_MS = 250;
const DISMISS_EASING = 'ease-out';

/**
 * Finds the closest scrollable parent element of the target element within the sheet.
 * The element must have overflow: auto/scroll and its content be taller/wider than the scrolling viewport.
 */
function findScrollableParentInSheet(
  target: HTMLElement,
  sheet: HTMLElement,
): HTMLElement | null {
  let element: HTMLElement | null = target;

  while (element && element !== sheet) {
    // eslint-disable-next-line @sail/ui-no-reflow
    const style = window.getComputedStyle(element);

    const hasOverflowY = ['auto', 'scroll'].includes(
      style.overflowY.toLowerCase(),
    );

    if (
      hasOverflowY &&
      // eslint-disable-next-line @sail/ui-no-reflow
      element.scrollHeight > element.clientHeight
    ) {
      return element;
    }

    // Move up to next parent
    element = element.parentElement;
  }

  return null;
}

/**
 * Applies resistance to the swipe down movement to achieve a more natural feel.
 * Beyond a certain threshold (RESISTANCE_START_PX), the resistance increases linearly.
 * This is a common mobile gesture pattern.
 */
function applyResistance(deltaY: number): number {
  if (deltaY <= RESISTANCE_START_PX) {
    return deltaY;
  }

  return (
    RESISTANCE_START_PX + (deltaY - RESISTANCE_START_PX) * RESISTANCE_FACTOR
  );
}

/**
 * Add swipe-to-dismiss gesture to the bottom-sheet
 *
 * - If the sheet has no scrollable content, it can be closed with a swipe-down gesture anywhere on the sheet.
 * - If the sheet has scrollable content:
 *   - If the scrollable content is at the top (scrollTop === 0), it can be closed with a swipe-down gesture anywhere on the sheet.
 *   - If the scrollable content is not at the top, it can only be closed with a swipe-down gesture that starts outside the scrollable area (e.g., in the header).
 *
 * Only active when `mobileDisplay === 'bottom-sheet'` and `requestClose` is defined.
 */
export function useSwipeDownToClose(
  element: HTMLDivElement | null,
  backdropElement: HTMLDivElement | null,
  requestClose: (() => void) | undefined,
  mobileDisplay: MobileDisplay,
  hasCloseConfirmation: boolean,
): void {
  useEffect(() => {
    if (mobileDisplay !== 'bottom-sheet' || !requestClose || !element) {
      return;
    }

    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    let startedInScrollable = false;
    let wasScrolling = false;
    let scrollableElement: HTMLElement | null = null;
    let sheetHeight = 0;

    function onTouchStart(e: TouchEvent): void {
      if (e.touches.length !== 1) {
        return;
      }

      const touch = e.touches[0];

      startY = touch.clientY;
      currentY = touch.clientY;
      isDragging = false;
      wasScrolling = false;

      scrollableElement = findScrollableParentInSheet(
        e.target as HTMLElement,
        element!,
      );

      startedInScrollable = !!scrollableElement;

      // eslint-disable-next-line @sail/ui-no-reflow
      sheetHeight = element!.offsetHeight;
    }

    function onTouchMove(e: TouchEvent): void {
      if (e.touches.length !== 1) {
        return;
      }

      const touch = e.touches[0];

      currentY = touch.clientY;

      const deltaY = currentY - startY;

      if (!isDragging) {
        // Ignore upward gestures, but track if the user is scrolling content
        if (deltaY <= 0) {
          if (
            startedInScrollable &&
            !!scrollableElement &&
            // eslint-disable-next-line @sail/ui-no-reflow
            scrollableElement.scrollTop > 0
          ) {
            wasScrolling = true;
          }

          return;
        }

        // Check if the scrollable content is scrolled past the top
        const scrolledPastTop =
          startedInScrollable &&
          !!scrollableElement &&
          // eslint-disable-next-line @sail/ui-no-reflow
          scrollableElement.scrollTop > 0;

        if (scrolledPastTop) {
          wasScrolling = true;
          return;
        }

        // If the user was scrolling content and reached the top, it's still a scroll, not a swipe
        if (wasScrolling) {
          return;
        }

        isDragging = true;
      }

      // If another handler already claimed this event, respect that and don't interfere
      if (e.defaultPrevented) {
        return;
      }

      // Prevent native scroll, otherwise it will trigger a bounce effect on the background
      e.preventDefault();

      const visualProgress = applyResistance(Math.max(0, deltaY));

      element!.style.transform = `translateY(${visualProgress}px)`;
      element!.style.transition = 'none';

      if (backdropElement && sheetHeight > 0) {
        const progress = Math.min(1, visualProgress / sheetHeight);
        backdropElement.style.opacity = String(1 - progress);
        backdropElement.style.transition = 'none';
      }
    }

    function snapBack(): void {
      element!.style.transition = `transform ${BOTTOM_SHEET_ANIMATION_DURATION_SHORT}ms ${BOTTOM_SHEET_ANIMATION_EASING}`;
      element!.style.transform = 'translateY(0)';

      if (backdropElement) {
        backdropElement.style.transition = `opacity ${BOTTOM_SHEET_ANIMATION_DURATION_SHORT}ms ${BOTTOM_SHEET_ANIMATION_EASING}`;
        backdropElement.style.opacity = '1';
      }

      const cleanup = () => {
        element!.style.transform = '';
        element!.style.transition = '';

        if (backdropElement) {
          backdropElement.style.opacity = '';
          backdropElement.style.transition = '';
        }

        element!.removeEventListener('transitionend', cleanup);
      };

      element!.addEventListener('transitionend', cleanup);
    }

    function onTouchEnd(): void {
      if (!isDragging) {
        return;
      }

      const deltaY = currentY - startY;

      isDragging = false;

      if (!requestClose || deltaY < DISMISS_THRESHOLD_PX) {
        snapBack();

        return;
      }

      if (hasCloseConfirmation) {
        snapBack();
        requestClose();

        return;
      }

      // Animate the sheet off-screen, basically close it by moving it 100% down
      element!.style.transition = `transform ${DISMISS_DURATION_MS}ms ${DISMISS_EASING}`;
      element!.style.transform = 'translateY(100%)';

      if (backdropElement) {
        backdropElement.style.transition = `opacity ${DISMISS_DURATION_MS}ms ${DISMISS_EASING}`;
        backdropElement.style.opacity = '0';
      }

      let called = false;

      const dismiss = (): void => {
        if (called) {
          return;
        }

        called = true;
        element!.removeEventListener('transitionend', dismiss);

        requestClose?.();
      };

      // Listen for the transition end event to call the requestClose callback
      element!.addEventListener('transitionend', dismiss);

      // Fallback in case `transitionend` does not fire, which means that requestClose hasn't been called, still mounting the sheet
      setTimeout(dismiss, DISMISS_DURATION_MS + 50);
    }

    element.addEventListener('touchstart', onTouchStart);

    element.addEventListener('touchmove', onTouchMove, {
      passive: false,
    });

    element.addEventListener('touchend', onTouchEnd);

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    element,
    backdropElement,
    mobileDisplay,
    requestClose,
    hasCloseConfirmation,
  ]);
}
